import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  getActividadesService,
  getActividadByIdService,
  createActividadService,
  updateActividadService,
  patchActividadService,
  deleteActividadService,
  entregarActividadService,
  revisarActividadService,
  deleteArchivoService,
  getMisEstudiantesService,
} from "../services/classroom/classroom.service";
import { ROLES } from "../routes/role.constants";

/**
 * Hook para gestionar la lógica del módulo Classroom (Actividades).
 * Encapsula comunicación con API, permisos por rol y estado local.
 */
const useClassroom = () => {
  const { user } = useSelector((state) => state.auth);
  const userRole = user?.rol?.trim();

  // ─── Permisos por rol ───
  const isMentor = userRole === ROLES.MENTOR;
  const isEstudiante = userRole === ROLES.ESTUDIANTE;
  const isSuperuser = userRole === ROLES.ADMIN_SYS;

  // El docente y superusuario pueden crear/editar/eliminar
  const canManage = isMentor || isSuperuser;
  // El alumno puede entregar
  const canSubmit = isEstudiante;
  // El docente puede revisar
  const canReview = isMentor || isSuperuser;

  // ─── Estado ───
  const [actividades, setActividades] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [ordering, setOrdering] = useState("-created_at");
  const [alumnoId, setAlumnoId] = useState("");
  const [estado, setEstado] = useState("");
  const [revision, setRevision] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [estudiantes, setEstudiantes] = useState([]);
  const [loadingEstudiantes, setLoadingEstudiantes] = useState(false);

  // ─── Cargar actividades ───
  const fetchActividades = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getActividadesService({
        search,
        ordering,
        alumnoId,
        estado,
        revision,
        desde,
        hasta,
      });
      // El servicio ya desenvuelve { count, results } → { actividades, count }
      // El listado no incluye "estado", se asume PENDIENTE por defecto
      const raw = data?.actividades ?? [];
      setActividades(raw.map((a) => ({ ...a, estado: a.estado || "PENDIENTE" })));
      setTotalCount(data?.count ?? 0);
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        "Error al cargar las actividades";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [search, ordering, alumnoId, estado, revision, desde, hasta]);

  useEffect(() => {
    fetchActividades();
  }, [fetchActividades]);

  // ─── Cargar estudiantes (solo mentor/superusuario) ───
  const fetchEstudiantes = useCallback(async (searchTerm = "") => {
    if (!canManage) return;
    setLoadingEstudiantes(true);
    try {
      const data = await getMisEstudiantesService(searchTerm);
      setEstudiantes(Array.isArray(data) ? data : []);
    } catch {
      setEstudiantes([]);
    } finally {
      setLoadingEstudiantes(false);
    }
  }, [canManage]);

  useEffect(() => {
    fetchEstudiantes();
  }, [fetchEstudiantes]);

  // ─── CRUD Actividades ───
  const createActividad = async (payload) => {
    try {
      await createActividadService(payload);
      toast.success("Actividad creada exitosamente");
      await fetchActividades();
    } catch (err) {
      const msg = extractError(err, "Error al crear la actividad");
      toast.error(msg);
      throw err;
    }
  };

  const updateActividad = async (id, payload) => {
    try {
      await updateActividadService(id, payload);
      toast.success("Actividad actualizada exitosamente");
      await fetchActividades();
    } catch (err) {
      const msg = extractError(err, "Error al actualizar la actividad");
      toast.error(msg);
      throw err;
    }
  };

  const patchActividad = async (id, payload) => {
    try {
      await patchActividadService(id, payload);
      toast.success("Actividad actualizada exitosamente");
      await fetchActividades();
    } catch (err) {
      const msg = extractError(err, "Error al actualizar la actividad");
      toast.error(msg);
      throw err;
    }
  };

  const deleteActividad = async (id) => {
    try {
      await deleteActividadService(id);
      toast.success("Actividad eliminada exitosamente");
      await fetchActividades();
    } catch (err) {
      const msg = extractError(err, "Error al eliminar la actividad");
      toast.error(msg);
      throw err;
    }
  };

  // ─── Entregar actividad (alumno) ───
  const entregarActividad = async (id, formData) => {
    try {
      const res = await entregarActividadService(id, formData);
      toast.success(res?.message || "Actividad entregada correctamente");
      await fetchActividades();
      return res;
    } catch (err) {
      const msg = extractError(err, "Error al entregar la actividad");
      toast.error(msg);
      throw err;
    }
  };

  // ─── Revisar entrega (docente) ───
  const revisarActividad = async (id, payload) => {
    try {
      const res = await revisarActividadService(id, payload);
      const msg = res?.message || (res?.accion === "validar" ? "Entrega validada correctamente" : "Entrega rechazada");
      toast.success(msg);
      await fetchActividades();
      return res;
    } catch (err) {
      const msg = extractError(err, "Error al revisar la entrega");
      toast.error(msg);
      throw err;
    }
  };

  // ─── Eliminar archivo de entrega (alumno) ───
  const deleteArchivo = async (actividadId, archivoId) => {
    try {
      const res = await deleteArchivoService(actividadId, archivoId);
      toast.success(res?.message || "Archivo eliminado correctamente");
      await fetchActividades();
      return res;
    } catch (err) {
      const msg = extractError(err, "Error al eliminar el archivo");
      toast.error(msg);
      throw err;
    }
  };

  return {
    // Estado
    actividades,
    totalCount,
    loading,
    error,
    search,
    ordering,
    alumnoId,
    estado,
    revision,
    desde,
    hasta,
    estudiantes,
    loadingEstudiantes,
    // Permisos
    userRole,
    isMentor,
    isEstudiante,
    isSuperuser,
    canManage,
    canSubmit,
    canReview,
    // Setters
    setSearch,
    setOrdering,
    setAlumnoId,
    setEstado,
    setRevision,
    setDesde,
    setHasta,
    // Acciones
    fetchActividades,
    createActividad,
    updateActividad,
    patchActividad,
    deleteActividad,
    entregarActividad,
    revisarActividad,
    deleteArchivo,
    getActividadById: getActividadByIdService,
  };
};

/** Extrae mensaje de error de la respuesta del servidor */
const extractError = (err, fallback) => {
  const data = err?.response?.data;
  if (!data) return fallback;

  // Caso: { "campo": ["error1", "error2"] }
  if (typeof data === "object") {
    const firstKey = Object.keys(data)[0];
    const firstVal = data[firstKey];
    if (Array.isArray(firstVal)) return firstVal[0];
    if (typeof firstVal === "string") return firstVal;
  }

  return data?.message || data?.detail || fallback;
};

export default useClassroom;
